// Imports
import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, PointElement, LinearScale, CategoryScale } from "chart.js";
import { FaUserCircle } from "react-icons/fa";
import { Card, CardContent } from "../components/ui/card";
import BalanceCard from "./BalanceCard";
import OrderBookSection from './OrderBookSection';
import Menu from './menu';
import axios from '../axiosInstance';
import jwtDecode from "jwt-decode"; // Decode JWT token
import rollercoinBanner from "../assets/images/bannerRollerCoin.gif"; // RollerCoin banner image
import logoBanner from "../assets/images/new_logo_without_font.png";
import { FaTelegramPlane, FaFacebookF, FaXTwitter } from "react-icons/fa6";
import HighPerformanceAd from "../components/HighPerformanceAd";
import { Sidebar } from "../components/Sidebar";
import { Header } from "../components/Header";
import KpiCards from "../components/KpiCards";


import { Footer } from "../components/ Footer";
// Register ChartJS components
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale);

// Generate random price history
const generatePriceHistory = () => {
  const priceHistory = [];
  let price = 100;
  for (let i = 0; i < 15; i++) {
    price += Math.random() * 20 - 10; // Random price variation
    price = Math.max(price, 50); // Don't go below 50
    priceHistory.push(price.toFixed(2));
  }
  return priceHistory.reverse(); // Reverse for newest first
};

// Main Marketplace Page component
export default function MarketplacePage({ onLogout }) {
  const [options, setOptions] = useState(0);
  const [coinz, setCoinz] = useState(0);
  const [weeklyProfit, setWeeklyProfit] = useState(0);
  const [selfWeeklyProfit, setSelfWeeklyProfit] = useState(0);
  const [balance, setBalance] = useState(300);
  const [priceHistory] = useState(generatePriceHistory());
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [orderType, setOrderType] = useState("buy");
  const [orderAmount, setOrderAmount] = useState(1);
  const [closedOrders, setClosedOrders] = useState([]);
  const [priceHistoryFromDB, setPriceHistoryFromDB] = useState([]);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Extract user ID from JWT
  const getUserIdFromToken = (token) => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.userId;
    } catch (error) {
      return null;
    }
  };

  // Fetch user data
  useEffect(() => {

    document.title = "MarketPlace | CoinzUp";

   axios.get(`/api/user/me`, { withCredentials: true }) 
      .then((response) => {
        const { coinz, options, systemCoinz, weeklyProfit } = response.data;
        const valeurCalculee = options > 0 ? (( systemCoinz / weeklyProfit) * options) : 0;  // On évite la division par 0
		
        setSelfWeeklyProfit(valeurCalculee);
        setCoinz(coinz);
        setOptions(options);
        setWeeklyProfit(weeklyProfit);
        // setLoading(false);
        // setLoading(false);

        // Fetch closed orders
        axios.get(`/api/orders/closed/me`,{ withCredentials: true }) 
          .then((res) => {
            const data = res.data;
            setClosedOrders(Array.isArray(data) ? data : []);

            // Fetch price history for chart
            axios.get(`/api/orders/average-prices`, )
              .then((res) => {
                setPriceHistoryFromDB(res.data);
              })
              .catch((err) => {
				  console.log(err);
				  
			  });
          })
          .catch((err) => {console.log(err);});
      })
      .catch((error) => {
		  console.log(error);
        setLoading(false);
      });
  }, []);

  // if (loading) return <div>Loading...</div>;
  // if (loading) return <div>Loading...</div>;

  // Chart data
  const data = {
    labels: priceHistoryFromDB.map(item => {
      const date = new Date(item.day);
      return date.toLocaleDateString();
    }),
    datasets: [
      {
        label: "Average option price (per day)",
        data: priceHistoryFromDB.map(item => parseFloat(item.averagePrice)),
        fill: false,
        borderColor: "#4CAF50",
        tension: 0.2,
        pointBackgroundColor: "#4CAF50",
        pointHoverRadius: 7,
        pointRadius: 4,
      },
    ],
  };

  const optionsChart = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        reverse: false,
        title: {
          display: true,
          text: "Date",
          color: "#ccc",
        },
      },
      y: {
        title: {
          display: true,
          text: "Average Price (COINZ)",
          color: "#ccc",
        },
        beginAtZero: false,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: context => `${context.parsed.y} COINZ`
        }
      }
    }
  };

  // Handle order placement
  const handlePlaceOrder = () => {
    if (orderAmount <= 0) return;

    if (orderType === "buy") {
      const optionToBuy = options.find(opt => opt.available > 0);
      if (optionToBuy && balance >= optionToBuy.buyPrice * orderAmount) {
        setBalance(prev => prev - optionToBuy.buyPrice * orderAmount);
        setOptions(prev =>
          prev.map(opt =>
            opt.id === optionToBuy.id
              ? { ...opt, available: opt.available - orderAmount }
              : opt
          )
        );
        setTransactionHistory(prev => [
          ...prev,
          {
            date: new Date().toLocaleDateString(),
            action: "Buy",
            option: optionToBuy.name,
            price: optionToBuy.buyPrice * orderAmount,
          },
        ]);
      }
    } else {
      const optionToSell = options.find(opt => (100 - opt.available) >= orderAmount);
      if (optionToSell) {
        setBalance(prev => prev + optionToSell.sellPrice * orderAmount);
        setOptions(prev =>
          prev.map(opt =>
            opt.id === optionToSell.id
              ? { ...opt, available: opt.available + orderAmount }
              : opt
          )
        );
        setTransactionHistory(prev => [
          ...prev,
          {
            date: new Date().toLocaleDateString(),
            action: "Sell",
            option: optionToSell.name,
            price: optionToSell.sellPrice * orderAmount,
          },
        ]);
      }
    }

    setOrderAmount(1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900 text-white font-sans">
      {/* Contenu principal avec sidebar */}
      <div className="flex flex-1 min-h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <Sidebar onLogout={onLogout}/>
  
        {/* Contenu principal */}
        <main className="flex-1 px-4 sm:px-10 py-8 space-y-10 overflow-auto min-w-0">
        <Header title="MarketPlace"  balance={coinz}        optionsOwned={options}        estimatedWeeklyGain={selfWeeklyProfit} />
          <HighPerformanceAd keyId="fd5da3543c594f07b73630f461f10613" width={728} height={90} />
 
          
  
          {/* Graphique d'évolution du prix */}
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Price Evolution & Rules</h2>
            <div className="w-full h-[400px] mb-6">
              <Line data={data} options={optionsChart} />
            </div>
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold mb-3">Holding Options & Earning Dividends</h3>
              <ul className="list-disc list-inside text-sm text-gray-300 space-y-2">
                <li>Buying options allows you to receive weekly dividends directly to your account.</li>
                <li>You must participate in at least <strong>10 draws per month</strong> to remain eligible.</li>
                <li>Inactive options are removed and returned to the market.</li>
                <li><span className="text-yellow-400 font-medium">Tip:</span> Sell your options before a long break.</li>
              </ul>
            </div>
          </div>
  
          {/* Order Book */}
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-sm">
            <OrderBookSection />
          </div>
  
          {/* Historique des ordres */}
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-sm">
            <h2 className="text-xl font-semibold mb-4">My Closed Orders</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left text-gray-300">
                <thead className="border-b border-gray-700 text-xs uppercase tracking-wider text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Price (COINZ)</th>
                    <th className="px-4 py-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {closedOrders.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-6 text-center text-gray-500">No closed orders.</td>
                    </tr>
                  ) : (
                    closedOrders.map((order) => (
                      <tr key={order.id} className="border-b border-gray-700 hover:bg-gray-700/20 transition">
                        <td className="px-4 py-2">{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td className={`px-4 py-2 font-semibold ${order.type === "buy" ? "text-green-400" : "text-red-400"}`}>
                          {order.type === "buy" ? "Buy" : "Sell"}
                        </td>
                        <td className="px-4 py-2">{order.price}</td>
                        <td className="px-4 py-2">{order.amount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
  
          <HighPerformanceAd keyId="96e99453a24be0353f2fe26dc0b9b728" width={468} height={60} />
        </main>
      </div>
  
      {/* Footer */}
      <Footer />
    </div>
  );
}
